import { Badge } from '@/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { formatTimelineTimestamp } from './timelineMath'

type PhaseDivider = {
  name: string
  at: number
  phaseIndex?: number
}

type EncounterEntry = {
  time: number
  phaseIndex: number
}

type AssignmentPhaseTimestampBadgeData = {
  phaseLabel?: string
  phaseName?: string
  timeLabel: string
}

const getPhaseFirstLetter = (phaseName: string) =>
  phaseName[0]?.toUpperCase() ?? 'P'

const buildPhaseLabel = (
  phaseIndex: number,
  phaseDividers: PhaseDivider[],
): string => {
  const phase =
    phaseDividers.find((divider) => divider.phaseIndex === phaseIndex) ??
    phaseDividers[phaseIndex]
  const phaseName = phase?.name?.trim() ?? ''
  const firstLetter = getPhaseFirstLetter(phaseName)
  const ordered = phaseDividers
    .map((divider, index) => ({
      divider,
      index: divider.phaseIndex ?? index,
    }))
    .sort((left, right) => left.index - right.index)
  let count = 0
  for (const entry of ordered) {
    if (getPhaseFirstLetter(entry.divider.name?.trim() ?? '') !== firstLetter) {
      continue
    }
    count += 1
    if (entry.index === phaseIndex) break
  }
  return `${firstLetter}${Math.max(count, 1)}`
}

const buildAssignmentPhaseTimestampBadgeData = ({
  castIndexes,
  entries,
  phaseDividers,
}: {
  castIndexes: number[]
  entries: EncounterEntry[]
  phaseDividers: PhaseDivider[]
}): AssignmentPhaseTimestampBadgeData => {
  const firstEntry = entries[castIndexes[0]]
  const phaseIndex = firstEntry?.phaseIndex ?? 0
  const phase =
    phaseDividers.find((divider) => divider.phaseIndex === phaseIndex) ??
    phaseDividers[phaseIndex]
  const phaseName = phase?.name?.trim() || undefined
  const phaseLabel =
    phaseDividers.length > 1
      ? buildPhaseLabel(phaseIndex, phaseDividers)
      : undefined
  const firstCastIndex = castIndexes[0]
  const lastCastIndex = castIndexes[castIndexes.length - 1]
  const firstTime = formatTimelineTimestamp(entries[firstCastIndex]?.time ?? 0)
  const lastTime = formatTimelineTimestamp(entries[lastCastIndex]?.time ?? 0)
  const timeLabel =
    castIndexes.length > 1 && firstTime !== lastTime
      ? `${firstTime} - ${lastTime}`
      : firstTime
  return { phaseLabel, phaseName, timeLabel }
}

const AssignmentPhaseTimestampBadge = ({
  phaseLabel,
  phaseName,
  timeLabel,
}: AssignmentPhaseTimestampBadgeData) => {
  if (!phaseLabel) {
    return <Badge variant='outline'>{timeLabel}</Badge>
  }
  const badge = (
    <Badge variant='outline' className='h-6 gap-0 p-0 overflow-hidden'>
      <span className='pl-1.5 pr-1 h-full inline-flex items-center'>
        {phaseLabel}
      </span>
      <span className='w-px h-full bg-border' />
      <span className='pl-1 pr-1.5 h-full inline-flex items-center bg-accent'>
        {timeLabel}
      </span>
    </Badge>
  )
  if (!phaseName) return badge
  return (
    <Tooltip>
      <TooltipTrigger asChild>{badge}</TooltipTrigger>
      <TooltipContent>{phaseName}</TooltipContent>
    </Tooltip>
  )
}

export type { PhaseDivider, AssignmentPhaseTimestampBadgeData }
export { AssignmentPhaseTimestampBadge, buildAssignmentPhaseTimestampBadgeData }
